// React
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux"; // Import useSelector

// Custom Form
import { DonationForm } from "./components/DonationForm";

// Layouts
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import PageContainer from "../../components/layouts/Containers/PageContainer";

// External Components
import { Elements } from "@stripe/react-stripe-js";

// UI
import DonateSuccess from "./components/DonateSuccess";
import SEO from "../../components/seo/SEO";

function DonateScreen({ stripePromise }) {
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(7);
  const [frequency, setFrequency] = useState("monthly");
  const [url, setUrl] = useState(window.location.pathname);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const canDoMonthly = Boolean(userInfo?.subscription?.customerId);
  const isSuccessRoute = url.endsWith("/donate-success");

  useEffect(() => {
    if (isSuccessRoute) {
      return;
    }

    setClientSecret("");

    if (frequency === "monthly") {
      if (!canDoMonthly) {
        return;
      }

      const priceLookup = {
        3: "price_1RrabXHUQmtkG68CJnalNDTm", // £3 - The Supporter
        7: "price_1RracdHUQmtkG68Cw00TVwk3", // £7 - The Steward
        15: "price_1Rraq3HUQmtkG68CuBlyMXKJ", // £15 - The Patron
      };

      const priceId = priceLookup[amount.toString()];

      if (!priceId) {
        console.error("Invalid amount for monthly subscription.");
        return;
      }

      // customerId is no longer sent: the server reads it from the signed-in
      // user's record, so a caller cannot subscribe someone else's Stripe
      // customer. credentials: "include" is what carries the auth cookie.
      fetch("https://api.nowasteddays.io/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ priceId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.error) throw new Error(data.error.message);
          setClientSecret(data.clientSecret);
        })
        .catch((error) => console.error("Error creating subscription:", error));
    } else {
      const amountInPence = Math.round(amount * 100);
      fetch("https://api.nowasteddays.io/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amountInPence }),
      })
        .then((res) => res.json())
        .then((data) => setClientSecret(data.clientSecret))
        .catch((error) =>
          console.error("Error fetching client secret:", error),
        );
    }
  }, [amount, frequency, userInfo, canDoMonthly, isSuccessRoute]);

  const appearance = {
    theme: "stripe",
    variables: {
      // Correctly uses the primary brand color for outlines/highlights
      colorPrimary: "#f5f5f5",
      // Correctly uses the neutral color for the input background
      colorBackground: "#f5f5f5",
      // Correctly uses the primary brand color for the text inside the input
      colorText: "#004a22",
      // Correctly uses the standard border radius
      borderRadius: "5px",
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  const renderBody = () => {
    if (isSuccessRoute) {
      const urlClientSecret = new URLSearchParams(window.location.search).get(
        "payment_intent_client_secret",
      );
      if (!urlClientSecret) {
        return (
          <div className="donation-tile" style={{ textAlign: "center" }}>
            <strong>Could not find payment details.</strong>
            <p>
              If you've just completed a donation and landed here, please get in
              touch via the contact page.
            </p>
            <a href="/today" style={{ color: "#004a22", fontWeight: 600 }}>
              Return to your journal
            </a>
          </div>
        );
      }
      return (
        <Elements
          options={{ clientSecret: urlClientSecret, appearance }}
          stripe={stripePromise}
        >
          <DonateSuccess />
        </Elements>
      );
    }

    if (frequency === "monthly" && !canDoMonthly) {
      return (
        <DonationForm
          mode="login-required"
          canDoMonthly={canDoMonthly}
          amount={amount}
          setAmount={setAmount}
          frequency={frequency}
          setFrequency={setFrequency}
        />
      );
    }

    if (clientSecret) {
      return (
        <Elements options={options} stripe={stripePromise} key={clientSecret}>
          <DonationForm
            mode="stripe"
            canDoMonthly={canDoMonthly}
            amount={amount}
            setAmount={setAmount}
            frequency={frequency}
            setFrequency={setFrequency}
          />
        </Elements>
      );
    }

    return (
      <DonationForm
        mode="stripe-loading"
        canDoMonthly={canDoMonthly}
        amount={amount}
        setAmount={setAmount}
        frequency={frequency}
        setFrequency={setFrequency}
      />
    );
  };

  return (
    <>
      <SEO
        title={"Support NoWastedDays"}
        description="Support NoWastedDays and allow as many people to benefit from the platform as possible."
        canonicalUrl={`https://www.nowasteddays.io/donate`}
      />
      <Header variant="light" active={true} />
      <PageContainer className="updateday--container">
        {renderBody()}
      </PageContainer>
      <TestFooter />
    </>
  );
}

export default DonateScreen;
